import React, { Component } from "react";
import PropTypes from "prop-types";
import { line as d3Line } from "d3-shape";

import GenericChartComponent from "../../GenericChartComponent";
import { getAxisCanvas } from "../../GenericComponent";
import { hexToRGBA, isDefined, strokeDashTypes } from "../../utils";

class BaselineLineSeries extends Component {
  constructor(props) {
    super(props);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
  }

  drawOnCanvas(ctx, moreProps) {
    const { yAccessor, defined, separatorPrice, greenStroke, redStroke } =
      this.props;
    const { interpolation } = this.props;

    const {
      xScale,
      chartConfig: { yScale },
      plotData,
      xAccessor,
    } = moreProps;

    if (separatorPrice == null) return;

    const greenColor = greenStroke?.color || "#4CAF50";
    const redColor = redStroke?.color || "#F44336";
    const strokeWidth = greenStroke?.width || redStroke?.width || 1;
    const strokeOpacity = greenStroke?.opacity || redStroke?.opacity || 1;

    ctx.lineWidth = strokeWidth;
    ctx.setLineDash([]);

    // Create line generator
    const lineSeries = d3Line()
      .defined((d) => defined(yAccessor(d)))
      .x((d) => Math.round(xScale(xAccessor(d))))
      .y((d) => Math.round(yScale(yAccessor(d))))
      .context(ctx);

    if (isDefined(interpolation)) {
      lineSeries.curve(interpolation);
    }

    // Draw line segments with color changes at separator crossings
    let currentSegment = [];
    let lastIsAbove = null;

    for (let i = 0; i < plotData.length; i++) {
      const d = plotData[i];
      const value = yAccessor(d);

      if (!defined(value)) continue;

      const isAbove = value >= separatorPrice;

      // If color should change, draw current segment and start new one
      if (
        lastIsAbove !== null &&
        lastIsAbove !== isAbove &&
        currentSegment.length > 0
      ) {
        // Draw current segment
        const color = lastIsAbove ? greenColor : redColor;
        ctx.strokeStyle = hexToRGBA(color, strokeOpacity);

        ctx.beginPath();
        lineSeries(currentSegment);
        ctx.stroke();

        // Start new segment with last point to ensure continuity
        currentSegment = [currentSegment[currentSegment.length - 1]];
      }

      currentSegment.push(d);
      lastIsAbove = isAbove;
    }

    // Draw final segment
    if (currentSegment.length > 0 && lastIsAbove !== null) {
      const color = lastIsAbove ? greenColor : redColor;
      ctx.strokeStyle = hexToRGBA(color, strokeOpacity);

      ctx.beginPath();
      lineSeries(currentSegment);
      ctx.stroke();
    }
  }

  render() {
    return (
      <GenericChartComponent
        clip={false}
        canvasToDraw={getAxisCanvas}
        canvasDraw={this.drawOnCanvas}
        drawOn={[]}
      />
    );
  }
}

BaselineLineSeries.propTypes = {
  yAccessor: PropTypes.func.isRequired,
  separatorPrice: PropTypes.number,
  greenStroke: PropTypes.shape({
    color: PropTypes.string,
    opacity: PropTypes.number,
    width: PropTypes.number,
    dash: PropTypes.oneOf(strokeDashTypes),
  }),
  redStroke: PropTypes.shape({
    color: PropTypes.string,
    opacity: PropTypes.number,
    width: PropTypes.number,
    dash: PropTypes.oneOf(strokeDashTypes),
  }),
  defined: PropTypes.func,
  interpolation: PropTypes.func,
};

BaselineLineSeries.defaultProps = {
  greenStroke: {
    color: "#4CAF50",
    opacity: 1,
    width: 1,
    dash: "Solid",
  },
  redStroke: {
    color: "#F44336",
    opacity: 1,
    width: 1,
    dash: "Solid",
  },
  defined: (d) => d != null,
};

export default BaselineLineSeries;
